<script setup>
/**
 * Where categories are kept. Filing happens on the transaction itself; this is
 * the list behind that — what exists, how much each is used, and the tags each
 * one hands down to everything filed under it.
 *
 * Renaming here moves the name across the whole ledger at once, because the
 * ledger stores the name. Deleting takes the label off and leaves the rows.
 */
import { computed, ref } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import Picker from '../ui/Picker.vue'
import { categoryUsage, setFilter } from '../../stores'
import { state, closeSheet, toast } from '../../stores/state'
import { t } from '../../stores/i18n'
import {
  addCategory,
  categoryTagIds,
  deleteCategory,
  findCategory,
  renameCategory,
  setCategoryTags,
} from '../../stores/categories'
import { addTag } from '../../stores/tags'

const draft = ref('')
const busy = ref(false)
const error = ref('')
const confirming = ref(null)
const editing = ref(null)
const editText = ref('')
const tagging = ref(null)

const categories = computed(() => state.categories)

/** How many transactions are filed under each, by name — the database counts. */
const used = categoryUsage

async function create() {
  const name = draft.value.trim()
  error.value = ''
  if (!name) return
  if (findCategory(name)) {
    error.value = t('categorySheet.exists', { name })
    return
  }
  busy.value = true
  await addCategory(name)
  busy.value = false
  draft.value = ''
  toast(t('categorySheet.created'), 'good')
}

function startEdit(category) {
  editing.value = category.id
  editText.value = category.name
  confirming.value = null
}

async function commitEdit(category) {
  const name = editText.value.trim()
  editing.value = null
  if (!name || name === category.name) return
  const clash = findCategory(name)
  if (clash && clash.id !== category.id) {
    error.value = t('categorySheet.exists', { name })
    return
  }
  await renameCategory(category.id, name)
  toast(t('categorySheet.renamed'), 'good')
}

/** Deleting leaves the transactions; they simply stop being filed anywhere. */
async function remove(category) {
  if (confirming.value !== category.id) {
    confirming.value = category.id
    return
  }
  busy.value = true
  await deleteCategory(category.id)
  busy.value = false
  confirming.value = null
  toast(t('categorySheet.deleted'))
}

function focusCategory(category) {
  setFilter('categories', [category.name])
  state.ui.panel = 'transactions'
  closeSheet()
}

/* ------------------------------------------------- the tags it hands down */

const tagLook = (id) => (categoryTagIds(tagging.value).includes(id) ? { tone: 'on' } : { tone: 'off' })

async function toggleTag(id) {
  const on = categoryTagIds(tagging.value)
  await setCategoryTags(
    tagging.value,
    on.includes(id) ? on.filter((t) => t !== id) : [...on, id],
  )
}

async function addNamedTag(name) {
  const tag = await addTag(name)
  if (tag) await setCategoryTags(tagging.value, [...categoryTagIds(tagging.value), tag.id])
}

const tagNames = (category) =>
  categoryTagIds(category.id)
    .map((id) => state.tags.find((t) => t.id === id)?.name)
    .filter(Boolean)
</script>

<template>
  <Sheet
    :title="t('charts.categories')"
    :subtitle="t('categorySheet.subtitle')"
    @close="closeSheet"
  >
    <div class="grid gap-4">
      <form class="flex items-end gap-2" @submit.prevent="create">
        <div class="min-w-0 flex-1">
          <label class="label" for="cat-new">{{ t('categorySheet.newCategory') }}</label>
          <input
            id="cat-new"
            v-model="draft"
            class="input"
            :placeholder="t('categorySheet.placeholder')"
            maxlength="40"
            @input="error = ''"
          />
        </div>
        <button class="btn-primary" type="submit" :disabled="!draft.trim() || busy">
          <Icon name="plus" :size="16" /> {{ t('common.add') }}
        </button>
      </form>

      <p v-if="error" class="-mt-2 text-[12.5px] text-danger">{{ error }}</p>

      <ul v-if="categories.length" class="grid gap-1.5">
        <li
          v-for="category in categories"
          :key="category.id"
          class="rounded-xl border border-line-soft bg-raised/50 py-2 pr-2 pl-3"
        >
          <div class="flex items-center gap-2">
            <input
              v-if="editing === category.id"
              v-model="editText"
              class="input !py-1.5 !text-[14px]"
              maxlength="40"
              @keydown.enter.prevent="commitEdit(category)"
              @blur="commitEdit(category)"
            />
            <template v-else>
              <button
                class="min-w-0 flex-1 truncate text-left text-[14px] font-medium"
                @click="focusCategory(category)"
              >
                {{ category.name }}
              </button>
              <span class="tnum shrink-0 text-[12px] text-faint">
                {{ used.get(category.name) ?? 0 }}
              </span>
              <button
                class="btn-ghost !px-2 !py-1"
                :aria-label="t('categorySheet.tagsOn', { name: category.name })"
                @click="tagging = tagging === category.id ? null : category.id"
              >
                <Icon name="filter" :size="15" :class="tagNames(category).length && 'text-accent'" />
              </button>
              <button
                class="btn-ghost !px-2 !py-1"
                :aria-label="t('common.renameThing', { name: category.name })"
                @click="startEdit(category)"
              >
                <Icon name="pencil" :size="15" />
              </button>
              <button
                class="btn-ghost !px-2 !py-1"
                :aria-label="t('common.deleteThing', { name: category.name })"
                @click="remove(category)"
              >
                <Icon v-if="confirming !== category.id" name="trash" :size="15" class="text-danger" />
                <span v-else class="text-[12px] font-semibold text-danger">{{ t('common.sure') }}</span>
              </button>
            </template>
          </div>

          <!-- Closed, the tags it hands down are just named; open, they are a
               picker like any other. -->
          <div
            v-if="tagging !== category.id && tagNames(category).length"
            class="mt-1.5 flex flex-wrap items-center gap-1 pl-0.5"
          >
            <span v-for="name in tagNames(category)" :key="name" class="tag bg-raised text-muted">
              {{ name }}
            </span>
          </div>
          <div v-if="tagging === category.id" class="mt-2">
            <label class="label" :for="`cat-tags-${category.id}`">
              {{ t('categorySheet.tagsOnEverything', { name: category.name }) }}
            </label>
            <Picker
              :id="`cat-tags-${category.id}`"
              :options="state.tags"
              :look="tagLook"
              :placeholder="t('common.searchTags')"
              @pick="toggleTag"
              @create="addNamedTag"
            />
          </div>
        </li>
      </ul>

      <p v-else class="rounded-xl border border-line-soft bg-raised/50 p-4 text-center text-[13px] text-muted">
        {{ t('categorySheet.empty') }}
      </p>

      <p class="text-[12px] text-faint">{{ t('categorySheet.hint') }}</p>
    </div>

    <template #footer>
      <div class="flex-1" />
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" @click="closeSheet">{{ t('common.done') }}<Kbd combo="mod+enter" /></button>
    </template>
  </Sheet>
</template>
